import type { RootState } from '@/store';
import { type AsyncThunkPayloadCreator, createAsyncThunk } from '@reduxjs/toolkit';

import { baixarPdfDoServidor } from '@/features/arbitral-detalhes';
import { buscarDefinicaoDoAgente, type Flow } from '@/features/negociacaoIndividualWizard';
import { getErrorMessage } from '@/shared/utils/error';
import { registrarAcao } from '@/shared/utils/reportAction';
import { reportError } from '@/shared/utils/reportError';
import {
  negociacoesService,
  type EncerrarAtendimentoHumanoResult,
  type EncerrarNegociacaoCasoResult,
  type EncerramentoNegociacaoInput,
  type ReaberturaNegociacaoInput,
  type ReabrirNegociacaoCasoResult,
} from '@/features/negociacoes';
import type {
  AssinaturaAcordoPainel,
  CancelNegociacaoStartResult,
  HistoricoDevedorResponse,
  JanelaAtendimento,
  NegociacaoAiInsights,
  NegociacaoCasoDetail,
  NegociacaoCasoTelefoneUi,
  RespondPendenciaResult,
  UpdateNegociacaoCasoInput,
  UpdateNegociacaoCasoResult,
} from '../types/negociacaoDetailTypes';
import type {
  CarregarPaginaDoCertificadoInput,
  CertificadoNegociacaoResumoDto,
  ErroDoCertificadoNegociacao,
  PaginaRecebidaDoCertificado,
  SecaoDoCertificado,
  SolicitarPdfDoCertificadoInput,
} from '../types/certificadoNegociacaoTypes';
import type { PaginaMarcos } from '../types/negociacaoMarcoTypes';
import { ehVinculoAReconciliar, fonteDoErroDoCertificado } from '../utils/certificadoNegociacao';
import { selectCertificadoNegociacaoBaixandoPdf } from './selectors';
import { ErroDaEmissaoDoCertificado, negociacaoDetailService } from './services';

const CODIGO_FONTE_INDISPONIVEL = 'CERTIFICADO_FONTE_INDISPONIVEL';

const STATUS_LIMITE_DE_CONSULTAS = 429;

function erroDaEmissao(causa: unknown): ErroDoCertificadoNegociacao {
  if (!(causa instanceof ErroDaEmissaoDoCertificado))
    return { status: null, code: null, fonte: null };
  return {
    status: causa.status,
    code: causa.codigo,
    fonte: fonteDoErroDoCertificado(causa.detalhes),
  };
}

const comoErro = (causa: unknown): Error =>
  causa instanceof Error ? causa : new Error(getErrorMessage(causa));

const errorEventIdDa = (causa: unknown): string | null =>
  causa instanceof ErroDaEmissaoDoCertificado ? causa.errorEventId : null;

/**
 * Fonte indisponível é desfecho previsto (o core não emite nada parcial, decisão provisória 5):
 * vai para `registrarAcao`. O 429 também: é o limite de consultas por pessoa e caso que o core
 * impõe, não falha. O 409 de vínculo a reconciliar idem: o core recusa a emissão até o
 * pertencimento das mensagens ser conferido (COR-05). 403 e 404 são previstos e já aparecem na
 * tela. O resto (5xx sem código conhecido, rede, resposta fora do contrato) é falha a investigar.
 */
function registrarFalhaDaEmissao(
  casoId: string,
  erro: ErroDoCertificadoNegociacao,
  causa: unknown,
): void {
  if (erro.code === CODIGO_FONTE_INDISPONIVEL) {
    registrarAcao('certificado_negociacao_indisponivel', { casoId, code: erro.code });
    return;
  }
  if (ehVinculoAReconciliar(erro)) {
    registrarAcao('certificado_negociacao_vinculo_a_reconciliar', { casoId });
    return;
  }
  if (erro.status === STATUS_LIMITE_DE_CONSULTAS) {
    registrarAcao('certificado_negociacao_limite_atingido', { casoId });
    return;
  }
  if (erro.status === 403 || erro.status === 404) return;
  reportError(comoErro(causa), {
    source: 'negociacao.certificado.emissao',
    casoId,
    status: erro.status,
    code: erro.code,
    errorEventId: errorEventIdDa(causa),
  });
}

type ConfigDaEmissao = { rejectValue: ErroDoCertificadoNegociacao };

const emitirCertificado: AsyncThunkPayloadCreator<
  CertificadoNegociacaoResumoDto,
  string,
  ConfigDaEmissao
> = async (casoId, { rejectWithValue }) => {
  try {
    return await negociacaoDetailService.obterEmissaoAtualDoCertificado(casoId);
  } catch (causa) {
    const erro = erroDaEmissao(causa);
    registrarFalhaDaEmissao(casoId, erro, causa);
    return rejectWithValue(erro);
  }
};

/** Abrir a aba ou a página: a emissão anterior sai da tela até a nova chegar. */
export const obterEmissaoAtualDoCertificado = createAsyncThunk<
  CertificadoNegociacaoResumoDto,
  string,
  ConfigDaEmissao
>('negociacaoDetail/obterEmissaoAtualDoCertificado', emitirCertificado);

/**
 * Um acontecimento novo com a consulta aberta: o mesmo pedido, mas a emissão exibida fica na tela
 * até a resposta chegar, para o documento não sumir a cada mensagem (ACC-01).
 */
export const atualizarEmissaoDoCertificado = createAsyncThunk<
  CertificadoNegociacaoResumoDto,
  string,
  ConfigDaEmissao
>('negociacaoDetail/atualizarEmissaoDoCertificado', emitirCertificado);

/**
 * O `switch` existe pela tipagem: amarra a seção pedida ao tipo do item que volta, sem cast.
 * `S` genérico não estreita a união `PaginaRecebidaDoCertificado`.
 */
async function lerPaginaDaSecao(
  input: CarregarPaginaDoCertificadoInput,
): Promise<PaginaRecebidaDoCertificado> {
  const ler = <S extends SecaoDoCertificado>(secao: S) =>
    negociacaoDetailService.obterPaginaDaSecaoDoCertificado(input, secao);
  switch (input.secao) {
    case 'campos':
      return { secao: 'campos', pagina: await ler('campos') };
    case 'contatos':
      return { secao: 'contatos', pagina: await ler('contatos') };
    case 'marcos':
      return { secao: 'marcos', pagina: await ler('marcos') };
    case 'mensagens':
      return { secao: 'mensagens', pagina: await ler('mensagens') };
  }
}

/**
 * Uma página de uma seção da emissão exibida (ACC-02). Toda falha aqui é inesperada: a emissão
 * existe e foi mostrada, e o conteúdo dela está congelado.
 */
export const carregarPaginaDaSecaoDoCertificado = createAsyncThunk<
  PaginaRecebidaDoCertificado,
  CarregarPaginaDoCertificadoInput,
  ConfigDaEmissao
>('negociacaoDetail/carregarPaginaDaSecaoDoCertificado', async (input, { rejectWithValue }) => {
  try {
    return await lerPaginaDaSecao(input);
  } catch (causa) {
    const erro = erroDaEmissao(causa);
    reportError(comoErro(causa), {
      source: 'negociacao.certificado.secao',
      casoId: input.casoId,
      emissaoId: input.emissaoId,
      secao: input.secao,
      status: erro.status,
      errorEventId: errorEventIdDa(causa),
    });
    return rejectWithValue(erro);
  }
});

/**
 * Baixa o PDF da emissão pedida pelo protocolo comum dos certificados (`baixarPdfDoServidor`). A
 * URL assinada fica dentro dele e nunca passa pelo estado.
 */
export const solicitarPdfDoCertificado = createAsyncThunk<
  void,
  SolicitarPdfDoCertificadoInput,
  { rejectValue: string; state: RootState }
>(
  'negociacaoDetail/solicitarPdfDoCertificado',
  async ({ casoId, emissaoId, nomeArquivo }, { rejectWithValue, signal }) => {
    try {
      await baixarPdfDoServidor(
        (sinal) => negociacaoDetailService.solicitarPdfDoCertificado(casoId, emissaoId, sinal),
        nomeArquivo,
        signal,
      );
    } catch (causa) {
      if (!signal.aborted) {
        reportError(causa instanceof Error ? causa : new Error(getErrorMessage(causa)), {
          source: 'negociacao.certificado.pdf',
          casoId,
          emissaoId,
        });
      }
      return rejectWithValue(getErrorMessage(causa, 'Falha ao gerar o PDF'));
    }
  },
  {
    // Um clique é uma solicitação: o duplo clique abriria uma segunda cadeia de sondagem.
    condition: (_arg, { getState }) => !selectCertificadoNegociacaoBaixandoPdf(getState()),
  },
);

export const fetchNegociacaoCasoDetail = createAsyncThunk<
  NegociacaoCasoDetail,
  string,
  { rejectValue: string }
>('negociacaoDetail/fetchCaso', async (caseId, { rejectWithValue }) => {
  try {
    return await negociacaoDetailService.getCasoById(caseId);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao carregar negociação');
  }
});

export const fetchNegociacaoTelefones = createAsyncThunk<
  NegociacaoCasoTelefoneUi[],
  string,
  { rejectValue: string }
>('negociacaoDetail/fetchTelefones', async (caseId, { rejectWithValue }) => {
  try {
    return await negociacaoDetailService.getTelefones(caseId);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : 'Não foi possível carregar os outros números. Tente novamente.',
    );
  }
});

export const fetchAssinaturaAcordo = createAsyncThunk<
  AssinaturaAcordoPainel,
  string,
  { rejectValue: string }
>('negociacaoDetail/fetchAssinaturaAcordo', async (caseId, { rejectWithValue }) => {
  try {
    return await negociacaoDetailService.getAssinaturaAcordo(caseId);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao carregar status de assinatura do acordo',
    );
  }
});

/**
 * O fluxo da barra lateral vem do catálogo de agentes publicados, e não de `/admin/flows/:id`:
 * a rota de admin devolvia o fluxo inteiro (prompt, funções) só ao admin-master e respondia 403 ao
 * cliente, e o 403 com workspace faz o `requestBuilder` apagar o escritório guardado e recarregar.
 */
export const fetchFluxoDaNegociacao = createAsyncThunk<Flow, string, { rejectValue: string }>(
  'negociacaoDetail/fetchFluxo',
  async (flowId, { rejectWithValue }) => {
    try {
      return await buscarDefinicaoDoAgente(flowId);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Erro ao carregar o fluxo da negociação',
      );
    }
  },
);

export const sendResumeTemplate = createAsyncThunk<
  { caseId: string; janelaAtendimento: JanelaAtendimento },
  string,
  { rejectValue: string }
>('negociacaoDetail/sendResumeTemplate', async (caseId, { rejectWithValue }) => {
  try {
    const result = await negociacaoDetailService.sendResumeTemplate(caseId);
    return { caseId, janelaAtendimento: result.janelaAtendimento };
  } catch (error) {
    return rejectWithValue(
      error instanceof Error
        ? error.message
        : 'Não foi possível enviar a mensagem. Tente novamente.',
    );
  }
});

export const fetchNegociacaoAiInsights = createAsyncThunk<
  NegociacaoAiInsights | null,
  { caseId: string; forceRefresh?: boolean },
  { rejectValue: string }
>('negociacaoDetail/fetchAiInsights', async ({ caseId, forceRefresh }, { rejectWithValue }) => {
  try {
    return await negociacaoDetailService.getAiInsights(caseId, { forceRefresh });
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao carregar insights de IA',
    );
  }
});

export const TIMEOUT_MARCOS_MS = 10_000;
const MENSAGEM_TIMEOUT_MARCOS = 'Tempo esgotado ao buscar os marcos desta negociação';

export const fetchNegociacaoMarcos = createAsyncThunk<
  PaginaMarcos,
  { caseId: string; cursor?: string },
  { rejectValue: string; state: RootState }
>(
  'negociacaoDetail/fetchMarcos',
  async ({ caseId, cursor }, { rejectWithValue, signal }) => {
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(
      () => timeoutController.abort(new Error(MENSAGEM_TIMEOUT_MARCOS)),
      TIMEOUT_MARCOS_MS,
    );

    try {
      return await negociacaoDetailService.getMarcos(caseId, {
        cursor,
        signal: AbortSignal.any([signal, timeoutController.signal]),
      });
    } catch (error) {
      if (timeoutController.signal.aborted) {
        return rejectWithValue(MENSAGEM_TIMEOUT_MARCOS);
      }
      return rejectWithValue(
        error instanceof Error ? error.message : 'Erro ao carregar os marcos desta negociação',
      );
    } finally {
      clearTimeout(timeoutId);
    }
  },
  {
    condition: ({ caseId, cursor }, { getState }) => {
      const marcos = getState().negociacaoDetail?.marcos;
      if (!marcos) return true;
      if (marcos.status === 'loading' && marcos.casoId === caseId) return false;
      if (cursor && marcos.casoId === caseId && !marcos.hasMore) return false;
      return true;
    },
  },
);

export const cancelNegociacaoStart = createAsyncThunk<
  CancelNegociacaoStartResult,
  { caseId: string },
  { rejectValue: string }
>('negociacaoDetail/cancelStart', async ({ caseId }, { rejectWithValue }) => {
  try {
    return await negociacaoDetailService.cancelStart(caseId);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao cancelar início');
  }
});

export const fetchNegociacaoConversaId = createAsyncThunk<
  string | null,
  { caso: NegociacaoCasoDetail },
  { rejectValue: string; state: RootState }
>('negociacaoDetail/fetchConversaId', async ({ caso }, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const user = state.auth.user;

    if (!user?.empresaId) {
      return rejectWithValue('Não autenticado');
    }

    const telefoneNormalizado = caso.counterpartyPhone.replace(/\D/g, '');

    return await negociacaoDetailService.getOrCreateConversaByCaseId({
      empresaId: caso.empresaId,
      escritorioId: caso.escritorioId,
      caseId: caso.id,
      telefoneNormalizado,
    });
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao carregar conversa');
  }
});

export const updateNegociacaoCaso = createAsyncThunk<
  UpdateNegociacaoCasoResult,
  { caseId: string; data: UpdateNegociacaoCasoInput },
  { rejectValue: string }
>('negociacaoDetail/updateCaso', async ({ caseId, data }, { rejectWithValue }) => {
  try {
    return await negociacaoDetailService.updateCaso(caseId, data);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao atualizar negociação');
  }
});

export const downloadNegociacaoReport = createAsyncThunk<
  void,
  { caseId: string; titulo: string },
  { rejectValue: string }
>('negociacaoDetail/downloadReport', async ({ caseId, titulo }, { rejectWithValue }) => {
  try {
    await negociacaoDetailService.downloadReport(caseId, titulo);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao baixar relatório');
  }
});

export const selecionarDirecionamento = createAsyncThunk<
  RespondPendenciaResult,
  { caseId: string; pendenciaId: string; selectedOptionId: string },
  { rejectValue: string }
>(
  'negociacaoDetail/selecionarDirecionamento',
  async ({ caseId, pendenciaId, selectedOptionId }, { rejectWithValue }) => {
    try {
      return await negociacaoDetailService.selecionarDirecionamento(
        caseId,
        pendenciaId,
        selectedOptionId,
      );
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Erro ao registrar direcionamento',
      );
    }
  },
);

export const encerrarNegociacaoDetail = createAsyncThunk<
  EncerrarNegociacaoCasoResult,
  EncerramentoNegociacaoInput,
  { rejectValue: string; state: RootState }
>('negociacaoDetail/encerrar', async (input, { rejectWithValue }) => {
  try {
    return await negociacoesService.encerrarCaso(input);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const encerrarAtendimentoHumanoDetail = createAsyncThunk<
  EncerrarAtendimentoHumanoResult,
  string,
  { rejectValue: string; state: RootState }
>('negociacaoDetail/encerrarAtendimentoHumano', async (caseId, { rejectWithValue }) => {
  try {
    return await negociacoesService.encerrarAtendimentoHumano(caseId);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const reabrirNegociacaoDetail = createAsyncThunk<
  ReabrirNegociacaoCasoResult,
  ReaberturaNegociacaoInput,
  { rejectValue: string; state: RootState }
>('negociacaoDetail/reabrir', async (input, { rejectWithValue }) => {
  try {
    return await negociacoesService.reabrirCaso(input);
  } catch (error) {
    return rejectWithValue(getErrorMessage(error));
  }
});

export const fetchOutrasNegociacoes = createAsyncThunk<
  HistoricoDevedorResponse,
  { caseId: string; page?: number; limit?: number },
  { rejectValue: string }
>(
  'negociacaoDetail/fetchOutrasNegociacoes',
  async ({ caseId, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      return await negociacaoDetailService.getHistoricoDevedor(caseId, page, limit);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
);

export const bootstrapNegociacaoDetail = createAsyncThunk<
  {
    caso: NegociacaoCasoDetail;
    conversaId: string | null;
    aiInsights: NegociacaoAiInsights | null;
  },
  { caseId: string },
  { rejectValue: string; state: RootState }
>('negociacaoDetail/bootstrap', async ({ caseId }, { dispatch, rejectWithValue }) => {
  try {
    const caso = await dispatch(fetchNegociacaoCasoDetail(caseId)).unwrap();
    const conversaId = await dispatch(fetchNegociacaoConversaId({ caso })).unwrap();
    const aiInsights = caso.aiInsights ?? null;

    return { caso, conversaId, aiInsights };
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao carregar negociação');
  }
});
